import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import { 
  Bell, 
  CheckCircle, 
  AlertCircle, 
  FileText, 
  Clock,
  XCircle,
  DollarSign
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationContext = createContext(null);

// Notification icons by type
const getNotificationIcon = (type, data) => {
  if (type === 'claim_status') {
    const status = data?.new_status;
    const icons = {
      submitted: Clock,
      under_review: FileText,
      pending_documents: AlertCircle,
      in_appraisal: DollarSign,
      approved: CheckCircle,
      denied: XCircle,
      paid: CheckCircle,
      closed: FileText
    };
    return icons[status] || Bell;
  }
  if (type === 'claim_submitted') return CheckCircle;
  if (type === 'document_request') return AlertCircle;
  return Bell;
};

// Get toast style by priority
const getToastStyle = (priority, type, data) => {
  if (priority === 'high') {
    if (data?.new_status === 'approved' || data?.new_status === 'paid') {
      return { className: 'bg-green-50 border-green-200' };
    }
    if (data?.new_status === 'denied' || type === 'document_request') {
      return { className: 'bg-amber-50 border-amber-200' };
    }
  }
  return {};
};

export function NotificationProvider({ children }) {
  const { getToken, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);

  // Show toast notification
  const showToast = useCallback((notification) => {
    const Icon = getNotificationIcon(notification.type, notification.data);
    const style = getToastStyle(notification.priority, notification.type, notification.data);
    
    toast(notification.title, {
      description: notification.message,
      icon: <Icon className="h-5 w-5" />,
      action: notification.data?.action_url ? {
        label: 'View',
        onClick: () => {
          window.location.href = notification.data.action_url;
        }
      } : undefined,
      duration: notification.priority === 'high' ? 10000 : 5000,
      ...style
    });
  }, []);

  // Handle incoming notification
  const handleNotification = useCallback((notification) => {
    // Skip connection messages
    if (notification.type === 'connected') {
      console.log('Real-time notifications connected');
      return;
    }

    // Skip keepalive messages
    if (notification.type === 'keepalive') {
      return;
    }

    // Prevent duplicate notifications by checking ID
    setNotifications(prev => {
      // Check if we already have this notification
      if (prev.some(n => n.id === notification.id)) {
        return prev;
      }
      // Add new notification at the beginning, keep last 50
      return [notification, ...prev].slice(0, 50);
    });
    
    // Update unread count only for new notifications
    if (!notification.read) {
      setUnreadCount(prev => prev + 1);
    }

    // Show toast only once per notification ID
    // Use a simple check to prevent duplicate toasts
    const toastKey = `toast_${notification.id}`;
    if (!sessionStorage.getItem(toastKey)) {
      sessionStorage.setItem(toastKey, 'true');
      showToast(notification);
    }
  }, [showToast]);

  // Connect to SSE endpoint
  const connect = useCallback(() => {
    const token = getToken();
    if (!token || !isAuthenticated) {
      return;
    }

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      // Create EventSource with auth header via fetch
      // Note: EventSource doesn't support custom headers, so we use fetch with streaming
      const controller = new AbortController();
      
      fetch(`${API_URL}/api/realtime/events`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache'
        },
        signal: controller.signal
      }).then(async response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        setIsConnected(true);
        reconnectAttempts.current = 0;

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            console.log('SSE stream ended');
            setIsConnected(false);
            scheduleReconnect();
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          
          // Process complete events (ending with \n\n)
          const events = buffer.split('\n\n');
          buffer = events.pop() || ''; // Keep incomplete event in buffer

          for (const event of events) {
            if (event.startsWith('data: ')) {
              try {
                const data = JSON.parse(event.slice(6));
                handleNotification(data);
              } catch (e) {
                // Ignore parse errors (might be keepalive)
              }
            }
          }
        }
      }).catch(error => {
        if (error.name !== 'AbortError') {
          console.error('SSE connection error:', error);
          setIsConnected(false);
          scheduleReconnect();
        }
      });

      // Store controller for cleanup
      eventSourceRef.current = { close: () => controller.abort() };

    } catch (error) {
      console.error('Failed to connect to real-time notifications:', error);
      scheduleReconnect();
    }
  }, [getToken, isAuthenticated, handleNotification]);

  // Schedule reconnection with exponential backoff
  const scheduleReconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    // Don't reconnect if we've hit the max attempts
    if (reconnectAttempts.current >= 10) {
      console.log('Max reconnect attempts reached, stopping reconnection');
      return;
    }

    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 60000); // Max 60 seconds
    reconnectAttempts.current += 1;

    console.log(`Scheduling reconnect in ${delay}ms (attempt ${reconnectAttempts.current})`);

    reconnectTimeoutRef.current = setTimeout(() => {
      if (isAuthenticated) {
        connect();
      }
    }, delay);
  }, [connect, isAuthenticated]);

  // Connect when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      connect();
    }

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [isAuthenticated, connect]);

  // Mark notification as read
  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  }, []);

  // Clear notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  const value = {
    notifications,
    unreadCount,
    isConnected,
    markAsRead,
    markAllAsRead,
    clearNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

export default NotificationContext;
